import axios from 'axios';
import { computed, ComputedRef, reactive } from "vue";

import { HttpRouter } from "@Modules/shared/interfaces/HttpRouter";

import DailyPopularityStats from "../DTOs/DailyPopularityStats";
import { FetchForAliasArgs, FetchForTagArgs, FetchForTeamArgs } from './types';

export type PopularityStatsData = { dataset: number[] };

export type iDailyPopularityStatsAPI = {
    dataset: ComputedRef<Readonly<DailyPopularityStats>>,
    loading: ComputedRef<boolean>,
    setRouter: (router: HttpRouter<'get'>) => iDailyPopularityStatsAPI,
    fetchForTeam: (args: FetchForTeamArgs) => Promise<void>
    fetchForAlias: (args: FetchForAliasArgs) => Promise<void>
    fetchForTag: (args: FetchForTagArgs) => Promise<void>
}

export function DefaultDailyPopularitystats(): number[] {
    let retval: number[] = [];
    for (let i = 0; i < 7; i++) {
        retval.push(i * 11);
    }

    return retval;
}

/**
 * A tool to allow you to fetch and interact with "popular times" analytics
 * data fetched from the server.
 */
export default function DailyPopularityStatsAPI(debug = false): iDailyPopularityStatsAPI {
    const state = reactive<{
        dataset: DailyPopularityStats,
        loading: boolean
    }>({
        dataset: new DailyPopularityStats(),
        loading: false,
    });

    let router: HttpRouter<'get'> = axios;

    async function _fetch(url: string, from: string, to: string): Promise<void> {
        state.loading = true;

        if (debug) {
            state.dataset = new DailyPopularityStats(
                DefaultDailyPopularitystats(),
                from,
                to
            );
        } else {
            const response: HttpResponse<PopularityStatsData> = await router.get(url);

            if (response.status !== 200) {
                throw new Error('Could not fetch daily popularity stats');
            }

            state.dataset = new DailyPopularityStats(response.data.dataset, from, to);
        }

        state.loading = false;
    }

    return {
        loading: computed(() => state.loading),
        dataset: computed(() => state.dataset),

        fetchForTeam: async function ({
            orgID,
            teamID = null,
            from,
            to,
            archived = false
        }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/popularity/weekday?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url, from, to);
        },

        fetchForAlias: async function ({
            orgID,
            teamID = null,
            alias,
            from,
            to,
            archived = false
        }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/popularity/weekday?alias[]=${encodeURIComponent(alias)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url, from, to);
        },

        fetchForTag: async function ({
            orgID,
            teamID = null,
            tag,
            from,
            to,
            archived = false
        }): Promise<void> {
            const url = `/app/api/organization/${orgID}/team/${teamID}/analytics/popularity/weekday?tag[]=${encodeURIComponent(tag)}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&archived=${archived}`;
            return _fetch(url, from, to);
        },

        /**
         * Allows you to swap out the router for a fake one when writing tests
         */
        setRouter(newRouter) {
            router = newRouter;
            return this;
        }
    };
}
